import React, { ElementType, FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type SxProps, type Theme } from '@mui/material';

import ChipMultiSelect from '@/components/fields/ChipMultiSelect';
import {
  type IndicatorChipProps,
  IndicatorChipVariant,
  IndicatorChipVariantTranslated
} from '@/components/statuses/IndicatorChip';

import { StatusChipIndicatorProps } from './createStatusChipIndicator';

interface StatusChipSelectProps {
  activeOptions?: string[];
}

interface ChipSelectComponentProps {
  ChipComponent?: ElementType<IndicatorChipProps & { value: string | number | null }>;
  options?: IndicatorChipVariantTranslated[];
  sx?: SxProps<Theme>;
}

const createStatusChipSelect = ({
  options,
  defaultSx,
  StatusIndicatorComponent,
  translationNamespace,
  ChipSelectComponent = ChipMultiSelect
}: {
  options: IndicatorChipVariant[];
  defaultSx: SxProps<Theme>;
  StatusIndicatorComponent: ElementType<StatusChipIndicatorProps>;
  translationNamespace?: string;
  ChipSelectComponent?: ElementType<ChipSelectComponentProps>;
}) => {
  const StatusIndicatorChip = StatusIndicatorComponent as ElementType<
    IndicatorChipProps & { value: string | number | null }
  >;
  const StatusChipSelect: FC<StatusChipSelectProps> = ({ activeOptions, ...props }) => {
    const { t } = useTranslation(translationNamespace);

    const activeStatuses = useMemo(() => {
      if (!activeOptions) return options;

      return activeOptions
        .map(optionValue => options.find(({ value }) => value === optionValue))
        .filter(Boolean) as IndicatorChipVariant[];
    }, [activeOptions]);

    const activeTranslatedStatuses = useMemo(
      () =>
        activeStatuses.map(status => {
          const { label } = status;

          return {
            ...status,
            label: typeof label === 'function' ? label(t) : label
          };
        }) as IndicatorChipVariantTranslated[],
      [activeStatuses, t]
    );

    return (
      <ChipSelectComponent
        ChipComponent={StatusIndicatorChip}
        options={activeTranslatedStatuses}
        sx={defaultSx}
        {...props}
      />
    );
  };

  return StatusChipSelect;
};

export default createStatusChipSelect;
